import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

/**
 * D4: 10DLC Registration Status Dashboard
 * Brand status, per-campaign status, throughput limits, rejection reasons.
 * Auto-refresh every 30s.
 */

interface BrandStatus {
  id: string;
  businessName: string;
  status: string;
  vettingScore: number | null;
  rejectionReason: string | null;
  submittedAt: string | null;
  verifiedAt: string | null;
}

interface CampaignStatus {
  id: string;
  useCase: string;
  description: string;
  status: string;
  throughputLimit: number | null;
  rejectionReason: string | null;
  submittedAt: string | null;
  registeredAt: string | null;
}

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  pending: { color: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]", label: "Pending Review" },
  verified: { color: "bg-[var(--color-success)]/10 text-[var(--color-success)]", label: "Verified" },
  registered: { color: "bg-[var(--color-success)]/10 text-[var(--color-success)]", label: "Registered" },
  carrier_propagated: { color: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]", label: "Carrier Propagated" },
  rejected: { color: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]", label: "Rejected" },
  suspended: { color: "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]", label: "Suspended" },
};

export default function TenDlcStatusDashboard() {
  const [brand, setBrand] = useState<BrandStatus | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [brandRes, campaignRes] = await Promise.all([
        apiFetch<{ data: BrandStatus[] }>("/v1/sms-registration/brands"),
        apiFetch<{ data: CampaignStatus[] }>("/v1/sms-registration/campaigns"),
      ]);
      setBrand(brandRes.data[0] ?? null);
      setCampaigns(campaignRes.data);
    } catch {
      // handled by empty state
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30_000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        {[1, 2].map((i) => <SkeletonCard key={i} className="h-24" />)}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">10DLC Registration Status</h1>
        <span className="text-xs text-muted-foreground">Auto-refreshes every 30s</span>
      </div>

      {/* Brand Status */}
      {brand ? (
        <div className="stagger-item mb-6 rounded-lg border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Brand: {brand.businessName}</h3>
              <p className="text-xs text-muted-foreground">
                Submitted {brand.submittedAt ? new Date(brand.submittedAt).toLocaleDateString() : "—"}
              </p>
            </div>
            <div className="text-right">
              <StatusBadge status={brand.status} />
              {brand.vettingScore !== null && (
                <p className="mt-1 text-xs text-muted-foreground">Trust score: {brand.vettingScore}/100</p>
              )}
            </div>
          </div>
          {brand.rejectionReason && (
            <div className="mt-3 rounded-md bg-[var(--color-destructive)]/5 p-3 text-sm text-[var(--color-destructive)]">
              <strong>Rejection reason:</strong> {brand.rejectionReason}
            </div>
          )}
        </div>
      ) : (
        <div className="mb-6 rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground">
          No brand registered. <a href="/settings/messaging/10dlc/brand" className="text-primary hover:underline">Register now</a>
        </div>
      )}

      {/* Campaigns */}
      <h3 className="mb-3 font-medium">Campaigns ({campaigns.length})</h3>
      {campaigns.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-6 text-center text-muted-foreground">
          No campaigns registered yet.
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <div key={c.id} className="stagger-item rounded-lg border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="rounded bg-muted px-2 py-0.5 text-xs font-mono">{c.useCase}</span>
                  <p className="mt-1 text-sm">{c.description.slice(0, 80)}{c.description.length > 80 ? "..." : ""}</p>
                </div>
                <div className="text-right">
                  <StatusBadge status={c.status} />
                  {c.throughputLimit && (
                    <p className="mt-1 text-xs text-muted-foreground">{c.throughputLimit} msg/s</p>
                  )}
                </div>
              </div>
              {c.rejectionReason && (
                <div className="mt-2 rounded-md bg-[var(--color-destructive)]/5 p-2 text-xs text-[var(--color-destructive)]">
                  {c.rejectionReason}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] ?? { color: "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]", label: status };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${config.color}`}>{config.label}</span>;
}
